import { useMutation, useQuery } from '@tanstack/react-query';
import { get, post, qs } from './client';
import type { ScraperConfig } from './types';

/** A raffle as returned by the test/preview endpoints — the dict shape that
 *  scrapers hand to send_raffles.raffle(), not a stored RaffleRecord. */
export interface PreviewRaffle {
  product?: string;
  link?: string;
  picture_url?: string;
  price?: string | number;
  subscription_cutoff_time?: number;
  collection_methods?: string[];
  sku?: string;
  start?: string | number;
  [k: string]: unknown;
}

export interface TemplateDetection {
  template: string;
  confidence: 'high' | 'medium' | 'low';
  reason?: string;
  error?: string;
}

export interface InspectResult {
  url: string;
  title?: string;
  card_candidates?: unknown[];
  field_candidates?: Record<string, unknown>;
  suggested_config?: Record<string, unknown>;
  previews?: unknown[];
  notes?: string[];
  suggested_name?: string;
  suggested_site_name?: string;
  error?: string;
}

export function useDetectTemplate(url: string, enabled: boolean) {
  return useQuery({
    queryKey: ['detect-template', url],
    queryFn: () => get<TemplateDetection>(`/api/scrapers/detect-template${qs({ url })}`),
    enabled: enabled && !!url,
    retry: false,
  });
}

/** Name availability. Returns {exists, source} where source is 'file' | 'config'. */
export function useNameExists(name: string) {
  return useQuery({
    queryKey: ['scraper-exists', name],
    queryFn: () =>
      get<{ exists: boolean; source: string | null }>(
        `/api/scraper/${encodeURIComponent(name)}/exists`,
      ),
    enabled: name.length > 1,
    retry: false,
  });
}

export function useCloneConfig() {
  return useMutation({
    mutationFn: (name: string) =>
      get<Pick<ScraperConfig, 'template' | 'interval_seconds' | 'config_json'>>(
        `/api/scraper/${encodeURIComponent(name)}/clone-config`,
      ),
  });
}

/** Guided builder: fetch a page and suggest selectors. */
export function useBuilderInspect() {
  return useMutation({
    mutationFn: (url: string) => post<InspectResult>('/api/scrapers/builder/inspect', { url }),
  });
}

/** Dry-run. Which endpoint applies depends on the template:
 *   custom  → /api/scrapers/test-custom       { code, config_json }
 *   guided  → /api/scrapers/builder/preview   { config_json }
 *   other   → /api/scrapers/<name>/test       { template, config_json }
 *
 *  Note the last one is PLURAL `scrapers`, unlike its eleven singular
 *  `/api/scraper/<name>/...` siblings. That inconsistency is in the backend —
 *  do not "correct" it here, it becomes a 404.
 */
export function useTestScraper() {
  return useMutation({
    mutationFn: ({
      template,
      name,
      code,
      config_json,
    }: {
      template: string;
      name: string;
      code?: string;
      config_json: Record<string, unknown>;
    }) => {
      if (template === 'custom') {
        return post<{ raffles?: PreviewRaffle[]; error?: string }>('/api/scrapers/test-custom', {
          code,
          config_json,
        });
      }
      if (template === 'guided') {
        return post<{ raffles?: PreviewRaffle[]; error?: string }>(
          '/api/scrapers/builder/preview',
          { config_json },
        );
      }
      return post<{ raffles?: PreviewRaffle[]; error?: string }>(
        `/api/scrapers/${encodeURIComponent(name)}/test`,
        { template, config_json },
      );
    },
  });
}

export function useSaveScraper() {
  return useMutation({
    mutationFn: ({
      template,
      name,
      code,
      config_json,
      interval_seconds,
    }: {
      template: string;
      name: string;
      code?: string;
      config_json: Record<string, unknown>;
      interval_seconds: number;
    }) => {
      if (template === 'custom') {
        return post<{ success: boolean; name: string }>('/api/scrapers/save-custom', {
          name,
          code,
          interval_seconds,
        });
      }
      return post<{ success: boolean; name: string }>('/api/scrapers/add', {
        name,
        template,
        config_json,
        interval_seconds,
      });
    },
  });
}
